import React, { useState, useEffect, useContext } from 'react';
import {
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { getBarcodeConfig, setBarcodeConfig } from '../../Actions';
import { AlertContext } from '../../Context/AlertContextProvider';

// Friendly labels for the whitelisted source columns returned by the server.
const COLUMN_LABELS = {
  unique_source_id: 'Source System ID',
  alternate_product_sku: 'Alternate Product SKU',
  product_sku: 'Product SKU',
  color_number: 'Color Number',
};

const BarcodeSettings = () => {
  const { handleAlertOpen, setMessageType, setMessage } =
    useContext(AlertContext);

  const [enabled, setEnabled] = useState(false);
  const [sourceColumn, setSourceColumn] = useState('unique_source_id');
  const [prefix, setPrefix] = useState('');
  const [symbology, setSymbology] = useState('CODE128');
  const [sourceColumns, setSourceColumns] = useState([]);
  // Nothing renders until the stored config arrives; otherwise the toggle
  // paints its default (off) and visibly flips once the call returns.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBarcodeConfig()
      .then((res) => {
        const data = res.data?.data || {};
        setEnabled(!!data.enabled);
        setSourceColumn(data.sourceColumn || 'unique_source_id');
        setPrefix(data.prefix || '');
        setSymbology(data.symbology || 'CODE128');
        setSourceColumns(res.data?.source_columns || []);
      })
      .catch(() => {
        setMessage('Failed to load barcode settings');
        setMessageType('error');
        handleAlertOpen();
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = () => {
    setBarcodeConfig({ enabled, sourceColumn, prefix, symbology })
      .then(() => {
        setMessage('Barcode settings updated');
        setMessageType('success');
        handleAlertOpen();
      })
      .catch(() => {
        setMessage('Failed to update barcode settings');
        setMessageType('error');
        handleAlertOpen();
      });
  };

  if (loading) return <CircularProgress size={24} />;

  return (
    <div style={{ padding: '10px 0' }}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 14 }}>
        Display a scannable barcode at the bottom of the product page. It is
        generated from the selected colour using the data column you choose, so
        RFMS can be searched with a scanner. Use the
        prefix field if your system expects a leading character.
      </Typography>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            margin: '20px 16px',
          }}
        >
          <ToggleButtonGroup
            value={enabled ? 'on' : 'off'}
            exclusive
            size="small"
            onChange={(e, newValue) => {
              if (newValue !== null) setEnabled(newValue === 'on');
            }}
          >
            <ToggleButton
              value="on"
              style={
                enabled
                  ? { width: '50px', backgroundColor: '#4caf50', color: '#FFFFFF' }
                  : { width: '50px', backgroundColor: '#00000017', color: 'initial' }
              }
            >
              Yes
            </ToggleButton>
            <ToggleButton
              value="off"
              style={
                !enabled
                  ? { width: '50px', backgroundColor: '#f44336', color: '#FFFFFF' }
                  : { width: '50px', backgroundColor: '#00000017', color: 'initial' }
              }
            >
              No
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography variant="body1">Show barcode on product page</Typography>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            alignItems: 'center',
            margin: '0 16px 20px',
          }}
        >
          <FormControl
            variant="filled"
            style={{ width: '80%', minWidth: 200, textAlign: 'left' }}
            disabled={!enabled}
          >
            <InputLabel id="barcode-source-label">Barcode Data Column</InputLabel>
            <Select
              labelId="barcode-source-label"
              value={sourceColumn}
              onChange={(e) => setSourceColumn(e.target.value)}
            >
              {sourceColumns.map((col) => (
                <MenuItem key={col} value={col}>
                  {COLUMN_LABELS[col] || col}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            variant="filled"
            label="Prefix (optional)"
            placeholder="e.g. P"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value)}
            disabled={!enabled}
            style={{ width: '80%', minWidth: 200 }}
            size="small"
            helperText="Prepended to the column value, e.g. P + 235893 → P235893"
          />

          <Button
            variant="contained"
            style={{ background: '#EEEEEE', color: '#009D47' }}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>

    </div>
  );
};

export default BarcodeSettings;
